/**
 * Transport-agnostic enums for the Partner Data Gateway module.
 *
 * These mirror the enums exposed by the Partner Data Gateway Service (PDGS)
 * and surfaced through operations-hub-bff as `Pdg*` GraphQL enums. Values
 * are kept identical to the wire representation so the adapter never has to
 * translate them.
 */

/**
 * Mirrors `PdgFileProcessingStatus`.
 *
 * Non-terminal phases run in order:
 *   PENDING → PARSING → VALIDATING → RESOLVING → FINALIZING → <terminal>
 *
 * Each post-parse phase names the work currently in flight upstream:
 * `Validating` is the file validation activity, `Resolving` the
 * identity/source resolution child workflows, `Finalizing` the join that
 * writes outbox events. `Paused` is orthogonal — only reachable from
 * `Parsing`.
 */
export enum FileProcessingStatus {
  Pending = 'PENDING',
  Parsing = 'PARSING',
  Validating = 'VALIDATING',
  Resolving = 'RESOLVING',
  Finalizing = 'FINALIZING',
  Paused = 'PAUSED',
  Cancelled = 'CANCELLED',
  Completed = 'COMPLETED',
  CompletedWithErrors = 'COMPLETED_WITH_ERRORS',
  Failed = 'FAILED',
  Rejected = 'REJECTED',
}

/**
 * Mirrors `PdgFileParsingBatchStatus`. Batches have their own lifecycle,
 * distinct from the parent file's `FileProcessingStatus`.
 */
export enum FileParsingBatchStatus {
  Pending = 'PENDING',
  Processing = 'PROCESSING',
  Completed = 'COMPLETED',
  Failed = 'FAILED',
  Skipped = 'SKIPPED',
}

/** Mirrors `PdgControlAction`. PDGS exposes no retry action. */
export enum ControlAction {
  Pause = 'PAUSE',
  Resume = 'RESUME',
  Cancel = 'CANCEL',
}

/** Mirrors `PdgConfigurationStatus`. */
export enum ConfigurationStatus {
  Draft = 'DRAFT',
  Published = 'PUBLISHED',
  Deleted = 'DELETED',
}
